import * as THREE from 'three';

import CameraManager from '../../widgets/timeline/cameramanager.js';
import RaytraceManager from '../../widgets/timeline/raytracemanager.js';
import HotSpots from '../../widgets/timeline/hotspots.js';
import CSSUX from '../../widgets/timeline/cssux.js';
import CategoryTrack from '../../widgets/timeline/categorytrack.js';
import Dates from '../../widgets/timeline/dates.js';
import Terrain from '../../widgets/timeline/terrain.js';

import ScreenParticles from '../../utils/screenparticles.js';

require('../../utils/animationapi.js');

import 'pepjs';

import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';

export default class Timeline {

    constructor(config) {

        let defaultConfig = {
            cmsMode: false,
            length:26000,
            width:4000,
            clearColor: 0x0c3333,
            skin: '/skins/default/',
            features: {},
            effects: {},
            cameras: {
                distToSelObject: 220,
                default: {
                    type: "PerspectiveCamera",
                    config: {
                        fov: 70,
                        ratio: window.innerWidth / window.innerHeight,
                        drawNear: .01,
                        drawFar: 30000,
                        posX: 0,
                        posY: 400,
                        posZ: 0,
                        focalLength: 3,
                        camera_offset: {x: 0, y: 0, z: 0},
                        targetX: 0,
                        targetY: -5000,
                        targetZ: 30000
                    }
                }
            },
            scene: {

                fog: {
                    type: 'Fog',
                    colour: 0x313244,
                    near: 500,
                    far: 28000
                }

            },
            renderer: {
                type: "WebGLRenderer",
                config: {
                    antialias: true,
                    alpha: false
                }
            },
            lights: {
                default: {
                    type: "HemisphereLight",
                    config: {
                        color: 0xffffff,
                        groundColor:0x2f3344,
                        intensity: 1
                    }
                },
            },
            materials: {}
        }

        // merge config with default
        this.config = deepmerge(defaultConfig, config);

        this._data = {};

        // instantiate the THREE.js environment
        this.inits();

        return this;

    }

    inits() {

        let self = this;

        // generate native dom and jquery references to the container
        this.container = document.getElementById("Timeline");
        //this.container.setAttribute( 'touch-action', 'none');
        this.$container = $(this.container);

        this.scene = new THREE.Scene();

        //let axesHelper = new THREE.AxesHelper( 1000 );
        //this.scene.add( axesHelper );

        //window.scene.background = new THREE.Color( this.config.clearColor );
        this.animating = false; // animating, any processor intensive shenanigans should only occour

        // fog
        if (this.config.scene.hasOwnProperty("fog")) {
            this.scene.fog = new THREE[this.config.scene.fog.type](new THREE.Color(this.config.scene.fog.colour), this.config.scene.fog.near, this.config.scene.fog.far);
        }

        // lights
        for(let light in this.config.lights){

            let light_conf = this.config.lights[light];
            let light_obj  = null;

            switch(light_conf.type){
                case 'AmbientLight':
                    light_obj  = new THREE[light_conf.type]( light_conf.config.color, light_conf.config.intensity );
                    break;
                case 'HemisphereLight':
                    light_obj  = new THREE[light_conf.type]( light_conf.config.color, light_conf.config.groundColor, light_conf.config.intensity );
                    break;
                case 'DirectionalLight':
                    light_obj  = new THREE[light_conf.type]( light_conf.config.color, light_conf.config.intensity );
                    light_obj.position.set(new THREE.Vector3(200000, light_conf.position.y, light_conf.position.z));
                    break;
                case 'PointLight':
                    light_obj  = new THREE[light_conf.type]( light_conf.config.color, light_conf.config.intensity, light_conf.config.distance, light_conf.config.decay );
                    light_obj.position.set(light_conf.position.x, light_conf.position.y, light_conf.position.z);
                    break;
            }
            this.scene.add(light_obj);

        }

        // default camera conf
        this.cameramanager = new CameraManager(this.config.cameras);

        // renderer
        this.renderer = new THREE.WebGLRenderer(this.config.renderer.config);
        this.renderer.setSize(this.container.offsetWidth, this.container.offsetHeight, true);
        this.renderer.setClearColor(this.config.clearColor, 1);
        this.renderer.setPixelRatio(window.devicePixelRatio);


        // Instantiate the base model
        this.scene.add(this.cameramanager.dolly);

        this.container.appendChild(this.renderer.domElement);

        window.addEventListener("orientationchange", this.resize.bind(this), false);
        window.addEventListener('resize', this.resize.bind(this), false);

        this.container.addEventListener( "pointerdown", function(event){  self.onDocumentPointerDown(event); });
        window.addEventListener( 'pointerdown', this.onDocumentPointerDown.bind(this), false );

        // listen for feature events
        document.addEventListener("feature_event", function (event) {
            self.doFeatureEvent(event);
        });


        this.raytracemanager = new RaytraceManager({container: this.config.container});
        this.hotspots = new HotSpots(this);
        this.cssux    = new CSSUX({});

        // create the dates
        this.dates = new Dates({});
        this.scene.add(this.dates.makeDates());

        // the depth (size) of the dates dictates the depth of the track
        this.categorytrack = new CategoryTrack({depth:this.dates.depth});
        this.scene.add(this.categorytrack.makeTrack());

        this.cameramanager.depth = this.dates.depth - 2000;

        // the depth (size) of the dates dictates the scale of the terrain which needs to scale over X in order to meet the end of the dates / track
        let terrainratio  = this.dates.depth / 26000; // 26000 is the size of the model in 3DS max

        this.setupTerrain(terrainratio);

        this.resize();
        this.animate();


    }

    animate(t) {

        let self = this;


        this.cameramanager.update();
        this.categorytrack.update();
        this.renderer.render(this.scene, this.cameramanager.camera);

        this.cssux.update();

        if ( this.mixer ) {
            let delta = this.clock.getDelta();
            this.mixer.update( delta );
        }

        //window.animationapi.animate(t);

        requestAnimationFrame(function (t) {
            self.animate(t);
        });

    }

    resize() {

        // resize the dom container via jquery
        this.$container.css({
            width: window.innerWidth,
            height: window.innerHeight,
        });

        this.$container.parent().css({
            width: window.innerWidth,
            height: window.innerHeight,
        });

        this.$container.parent().parent().css({
            width: window.innerWidth,
            height: window.innerHeight,
        });

        // update the camera
        this.cameramanager.camera.aspect = window.innerWidth / window.innerHeight;
        this.cameramanager.camera.updateProjectionMatrix();

        if (this.cameramanager.camera.aspect > 1) {
            $("body").removeClass("portrait");
        } else {
            $("body").addClass("portrait");
        }

        $('iframe').css({
            height: window.innerHeight,
            width: window.innerWidth
        });

        // update the renderer
        this.renderer.setSize(window.innerWidth, window.innerHeight);


    }

    setupTerrain(scaleX){

        // do screen particles
        /*
        this.screenparticles = new ScreenParticles({});
        let particles = this.screenparticles.doMakeParticles();
        this.cameramanager.dolly.add(particles);

         */
        scaleX +=.2; // give us some wiggle room

        let filename = '/3d/terrain.stl'; //'/3d/terrain.stl';
        let objLoader = (filename.indexOf(".stl") != -1) ? new FBXLoader() : new OBJLoader();
        let self      = this;

        objLoader.load(
            filename,
            function (object) {

                let terrain  = new Terrain({});
                self.terrain = terrain.getTerrain(object);
                self.scene.add(self.terrain);

                self.terrain.scale.set(1,scaleX,.8);
                self.terrain.rotation.z = -Math.PI;
                self.terrain.rotation.x = -Math.PI / 2;
                self.terrain.position.z = (26000 * scaleX) / 2;

             },
            function (xhr) {

             },
            // error
            function (error) {
                //console.dir(error );
            }
        );

    }


    getPointInBetweenByLen(pointA, pointB, length) {

        let dir = pointB.clone().sub(pointA).normalize().multiplyScalar(length);
        return pointA.clone().add(dir);

    }

    onDocumentPointerDown(event) {

    }

    getParam(name){
        let match = RegExp('[?&]' + name + '=([^&]*)').exec(window.location.search);
        return match && decodeURIComponent(match[1].replace(/\+/g, ' '));
    }

    doFeatureEvent(event){
        if (event.detail.hasOwnProperty("action") && event.detail.action == "reset") {
            this.cameramanager.resetCam();
        }
    }


}



